import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SortOrder } from 'antd/es/table/interface';
import { Button, Table } from 'antd';
import cx from 'classnames';
import pluralize from 'pluralize';
import queryString from 'query-string';
import { useDidUpdate } from 'rooks';
import moment from 'moment';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/lib/table/interface';

import { SortingDirectionEnum } from 'enums';
import {
  AgingDetailTotalViewModel,
  AgingTotalInfoViewModel,
} from 'api/backofficeApiClient';
import { useAppDispatch } from 'hooks/useAppDispatch';
import SortButton from 'components/wrappers/BaseSortButton';
import { getPeriodTitle } from 'utils/reportUtils';
import {
  getAgingDetailTotalReport,
  getAgingDetailTotalReportExcel,
} from 'redux/reducers/homeDashboardSlice';
import { pageDefaultSizeTable } from 'constants/index';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import LastSyncDateSection from 'components/LastSyncDateSection';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import {
  getRowSpanValue,
  isShowFooterTableOnLastPage,
} from 'utils/tableSettings';
import { downloadFileByObjectURL } from 'utils/fileUtils';

import styles from './AgingReportTotalDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

export default function AgingReportTotalDetailsPage() {
  const { period } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { sortOrder } = queryString.parse(location.search);
  const [sortParam, setSortParam] = useState<SortOrder | undefined>(
    sortOrder || SortingDirectionEnum.Ascend,
  );
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [
    isAgingReportTotalDetailsDataLoading,
    setIsAgingReportTotalDetailsDataLoading,
  ] = useState(false);
  const [agingReportTotalDetailsData, setAgingReportDetailsData] =
    useState<AgingTotalInfoViewModel>();
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const dispatch = useAppDispatch();

  const agingReportCountMessage = pluralize(
    'record',
    agingReportTotalDetailsData?.totalCount,
    true,
  );

  useDidUpdate(() => {
    const queryData = {
      sortOrder: sortParam,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [sortParam]);

  const loadAgingReportTotalDetailsData = useCallback(async () => {
    if (!period) return;

    setIsAgingReportTotalDetailsDataLoading(true);

    const result = await dispatch(
      getAgingDetailTotalReport({
        pageNumber: currentTablePage - 1,
        sortType: getSortOrderRequestValue(sortParam),
        dateRangeType: +period,
        pageSize: pageDefaultSizeTable,
      }),
    );
    if (getAgingDetailTotalReport.fulfilled.match(result)) {
      setAgingReportDetailsData(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsAgingReportTotalDetailsDataLoading(false);
  }, [dispatch, currentTablePage, sortParam, period]);

  const loadInitialData = useCallback(async () => {
    await loadAgingReportTotalDetailsData();

    setIsInitialDataLoaded(true);
  }, [loadAgingReportTotalDetailsData]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const columns = useMemo(
    () => [
      {
        className: 'custom-table-cell--union',
        title: (
          <SortButton
            isActive={true}
            sortType={sortParam}
            label="Customer Name"
          />
        ),
        key: 'customerName',
        sorter: true,
        sortDirections: [
          SortingDirectionEnum.Ascend,
          SortingDirectionEnum.Descend,
        ],
        sortOrder: sortParam,
        render: (
          record: AgingDetailTotalViewModel,
          row: AgingDetailTotalViewModel,
          index: number,
        ) => {
          return {
            props: {
              rowSpan: getRowSpanValue(
                index,
                agingReportTotalDetailsData?.details || [],
              ),
            },
            children: <span>{record.customerCompanyName}</span>,
          };
        },
      },
      {
        className: 'custom-table-cell--union',
        title: 'Customer ID',
        key: 'customerId',
        render: (
          record: AgingDetailTotalViewModel,
          row: AgingDetailTotalViewModel,
          index: number,
        ) => {
          return {
            props: {
              rowSpan: getRowSpanValue(
                index,
                agingReportTotalDetailsData?.details || [],
              ),
            },
            children: <span>{record.customerKey}</span>,
          };
        },
      },
      {
        className: 'custom-table-cell--union',
        title: 'Terms',
        key: 'terms',
        render: (
          record: AgingDetailTotalViewModel,
          row: AgingDetailTotalViewModel,
          index: number,
        ) => {
          return {
            props: {
              rowSpan: getRowSpanValue(
                index,
                agingReportTotalDetailsData?.details || [],
              ),
            },
            children: <span>{record?.terms}</span>,
          };
        },
      },
      {
        title: 'Worker Name',
        key: 'workerName',
        render: (record: AgingDetailTotalViewModel) => record.workerName,
      },
      {
        title: 'Document Number',
        key: 'documentNumber',
        render: (record: AgingDetailTotalViewModel) => record.invoice,
      },
      {
        title: 'Invoice Date',
        key: 'invoiceDate',
        render: (record: AgingDetailTotalViewModel) =>
          record.invDate ? moment.utc(record.invDate).format('MM/DD/YYYY') : '',
      },
      {
        title: 'Due Date',
        key: 'dueDate',
        render: (record: AgingDetailTotalViewModel) =>
          record.dueDate ? moment.utc(record.dueDate).format('MM/DD/YYYY') : '',
      },
      {
        title: 'Age',
        key: 'age',
        render: (record: AgingDetailTotalViewModel) => record.age,
      },
      {
        title: getPeriodTitle(period),
        key: getPeriodTitle(period),
        render: (record: AgingDetailTotalViewModel) => record.balance,
      },
    ],
    [sortParam, period, agingReportTotalDetailsData],
  );

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort:
      | SorterResult<AgingDetailTotalViewModel>
      | SorterResult<AgingDetailTotalViewModel>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<AgingDetailTotalViewModel>).order ||
      SortingDirectionEnum.Ascend;

    setSortParam((prevSortOrder) => {
      if (prevSortOrder !== sortOrder) {
        setCurrentTablePage(1);
      } else {
        setCurrentTablePage(pagination.current || 1);
      }
      return sortOrder;
    });
  };

  const handleDownloadReport = async () => {
    if (!period) return;

    const result = await dispatch(
      getAgingDetailTotalReportExcel({
        pageNumber: currentTablePage - 1,
        sortType: getSortOrderRequestValue(sortParam),
        pageSize: pageDefaultSizeTable,
        dateRangeType: +period,
      }),
    );

    if (getAgingDetailTotalReportExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  if (!agingReportTotalDetailsData) return null;

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {agingReportTotalDetailsData?.lastSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={agingReportTotalDetailsData.lastSyncDate}
                title="Aging report is automatically refreshed every 10 minutes."
              />
            </div>
          )}
        </BasePageLabel>

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              enableSubTitle={isInitialDataLoaded}
              subTitle={agingReportCountMessage}
              title="AR Aging Report - Total"
            >
              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>
            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: agingReportTotalDetailsData?.totalCount,
                pageSize: pageDefaultSizeTable,
                current: currentTablePage,
                onChange: setCurrentTablePage,
                showSizeChanger: false,
              }}
              showSorterTooltip={false}
              summary={() => {
                if (
                  !isInitialDataLoaded ||
                  !isShowFooterTableOnLastPage(
                    agingReportTotalDetailsData?.totalCount,
                    pageDefaultSizeTable,
                    agingReportTotalDetailsData?.pageNumber,
                  )
                )
                  return;

                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={8} index={1}>
                      Total
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      {agingReportTotalDetailsData?.totalBalance}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
              onChange={handleTableChange}
              columns={columns}
              dataSource={agingReportTotalDetailsData?.details}
              rowKey={(record) => record.invoice as string}
              loading={isAgingReportTotalDetailsDataLoading}
              scroll={{ x: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
